import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDownUp, Check, Download, ListFilter } from 'lucide-react'
import api from '../api/client'
import DataTable from '../golive/components/DataTable'
import { JobStatusBadge, JobTypeBadge } from './components/JobBadges'
import { downloadDocument } from './utils/commonUtil'
import { ACTIVE_STATUSES, STATUS_GROUPS } from './utils/constants'

const JobRunHistoryPage = () => {
  // Converts a Unix epoch timestamp (seconds) to Date and time.
  // Queued jobs have started_at = 0.0 (pipeline hasn't started), so those return '—'.
  function _formatStartedAt(started_at) {
    if (!started_at || started_at <= 0) return '—'
    return new Date(started_at * 1000).toLocaleString()
  }

  const filterRef = useRef(null)

  const [jobs,           setJobs]           = useState([])
  const [_loading,       setLoading]        = useState(true)
  const [error,          setError]          = useState(null)
  const [statusFilter,   setStatusFilter]   = useState('all')
  const [typeFilter,     setTypeFilter]     = useState('all')
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [sortOrder,      setSortOrder]      = useState('desc')

  useEffect(() => {
    const handleOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowFilterMenu(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  const fetchJobs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { sort_order: sortOrder }
      if (statusFilter !== 'all') params.status   = statusFilter
      if (typeFilter   !== 'all') params.job_type = typeFilter
      const { data } = await api.get('/compliance/jobs', { params })
      setJobs(data || [])
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load job history.')
    } finally {
      setLoading(false)
    }
  }, [sortOrder, statusFilter, typeFilter])

  const pollJobs = useCallback(async () => {
    try {
      const params = { sort_order: sortOrder }
      if (statusFilter !== 'all') params.status   = statusFilter
      if (typeFilter   !== 'all') params.job_type = typeFilter
      const { data } = await api.get('/compliance/jobs', { params })
      setJobs(data || [])
    } catch (err) {
      console.error('Polling error', err)
    }
  }, [sortOrder, statusFilter, typeFilter])

  useEffect(() => { fetchJobs() }, [fetchJobs])

  const hasActiveJobs = jobs.some(j => ACTIVE_STATUSES.has(j.status))
  useEffect(() => {
    if (!hasActiveJobs) return
    const interval = setInterval(pollJobs, 5000)
    return () => clearInterval(interval)
  }, [hasActiveJobs, pollJobs])

  const handleDownload = useCallback(async (jobId, jobType, sowFilename) => {
    try {
      const baseName = (sowFilename ?? '').replace(/\.[^.]+$/, '') || jobId.slice(0, 8)
      const filename = jobType === 'raid' ? `RAID_${baseName}.xlsx`
                     : jobType === 'tvr'  ? `TVR_${baseName}.xlsx`
                     : `SMP_${baseName}.docx`
      await downloadDocument(jobId, filename)
    } catch (err) {
      if (err?.message) setError(err.message)
    }
  }, [])

  const handleStatusFilter = useCallback((value) => {
    setStatusFilter(value)
    setShowFilterMenu(false)
  }, [])

  const handleTypeFilter = useCallback((value) => {
    setTypeFilter(value)
    setShowFilterMenu(false)
  }, [])

  const columns = useMemo(() => [
    {
      id: 'run',
      header: 'Run',
      width: 56,
      align: 'center',
      sortAllowed: false,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-slate-700">
          {row.index + 1}
        </span>
      ),
    },
    {
      accessorKey: 'job_id',
      header: 'Job ID',
      width: 130,
      sortAllowed: false,
      cell: ({ getValue }) => {
        const id = getValue()
        return (
          <div className="relative group">
            <span className="font-mono text-xs text-slate-500">{id.slice(0, 8)}…</span>
            <div className="absolute left-0 bottom-full mb-1.5 z-50 hidden group-hover:block">
              <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-1.5 shadow-xl whitespace-nowrap font-mono">
                {id}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'sow_filename',
      header: 'SOW Document',
      sortAllowed: false,
      cell: ({ getValue }) => (
        <span className="text-sm text-slate-700 truncate block max-w-55" title={getValue()}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'job_type',
      header: 'Type',
      width: 90,
      align: 'center',
      sortAllowed: false,
      cell: ({ getValue }) => <JobTypeBadge type={getValue()} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      width: 130,
      sortAllowed: false,
      cell: ({ getValue }) => <JobStatusBadge status={getValue()} />,
    },
    {
      accessorKey: 'started_at',
      header: 'Started At',
      width: 175,
      sortAllowed: false,
      cell: ({ getValue }) => _formatStartedAt(getValue()),
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 100,
      align: 'center',
      sortAllowed: false,
      cell: ({ row }) => {
        const { job_id, job_type, sow_filename, status } = row.original
        const canDownload = status === 'completed'
        return (
          <div className="flex items-center justify-center">
            <button
              type="button"
              aria-label="Download result"
              disabled={!canDownload}
              onClick={() => handleDownload(job_id, job_type, sow_filename)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-slate-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed outline-none focus:outline-none"
            >
              <Download size={12} />
              Download
            </button>
          </div>
        )
      },
    },
  ], [handleDownload])

  const toolbarContent = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        title={sortOrder === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
        onClick={() => setSortOrder(s => s === 'desc' ? 'asc' : 'desc')}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-xs whitespace-nowrap outline-none focus:outline-none focus-visible:outline-none"
      >
        <ArrowDownUp size={13} />
        {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
      </button>

      <div className="relative" ref={filterRef}>
        <button
          type="button"
          onClick={() => setShowFilterMenu(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 transition-colors text-sm outline-none focus:outline-none focus-visible:outline-none"
        >
          <ListFilter size={15} />
          {(statusFilter !== 'all' || typeFilter !== 'all') && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
          )}
        </button>

        {showFilterMenu && (
          <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 min-w-44">
            {/* Single "All" — resets both filters */}
            <button
              type="button"
              onClick={() => { setTypeFilter('all'); setStatusFilter('all'); setShowFilterMenu(false) }}
              className={[
                'w-full flex items-center justify-between px-3 py-1 text-xs transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none',
                typeFilter === 'all' && statusFilter === 'all'
                  ? 'bg-blue-50 text-blue-600 font-medium'
                  : 'bg-white text-slate-500 hover:bg-gray-200',
              ].join(' ')}
            >
              <span>All</span>
              {typeFilter === 'all' && statusFilter === 'all' && (
                <Check size={11} className="text-blue-500 shrink-0" />
              )}
            </button>

            {/* Type section */}
            <div className="border-t border-gray-100 mt-1 mb-0.5" />
            <p className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
              Type
            </p>
            {['smp', 'raid', 'tvr'].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleTypeFilter(value)}
                className={[
                  'w-full flex items-center justify-between gap-2 px-3 py-0.5 transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none',
                  typeFilter === value ? 'bg-blue-50' : 'bg-white hover:bg-gray-200',
                ].join(' ')}
              >
                <JobTypeBadge type={value} />
                {typeFilter === value && <Check size={11} className="text-blue-500 shrink-0" />}
              </button>
            ))}

            {/* Status section */}
            <div className="border-t border-gray-100 mt-1 mb-0.5" />
            <p className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
              Status
            </p>
            {STATUS_GROUPS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => handleStatusFilter(value)}
                className={[
                  'w-full flex items-center justify-between gap-2 px-3 py-0.5 transition-colors border-0 outline-none focus:outline-none focus-visible:outline-none',
                  statusFilter === value ? 'bg-blue-50' : 'bg-white hover:bg-gray-200',
                ].join(' ')}
              >
                <JobStatusBadge status={value} />
                {statusFilter === value && <Check size={11} className="text-blue-500 shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      {error && (
        <div className="mb-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl">
        <DataTable
          title="Job History"
          isSorted={false}
          canSearch={false}
          showTotalEntries={false}
          autoResetPageIndex={false}
          toolbarContent={toolbarContent}
          table={{ columns, rows: jobs }}
        />
      </div>
    </>
  )
}

export default JobRunHistoryPage
